import { useState, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { Credits } from '../../components/Credits';
import { Steve } from '../../components/Steve';
import { VuMeter } from '../../components/VuMeter';
import { S, formatPrice, t } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { useActions, useSteve } from '../../state/context';
import { useAudioText } from '../../state/derived';
import { SaasNewsletter } from './SaasNewsletter';
import { SaasSignupModal } from './SaasSignupModal';
import styles from './saas.module.css';

/** Skin 3 — SaaS 2026 : la landing produit d'un poisson qui a levé une série A. */
export function SaasSkin(): ReactNode {
  const { lang, seen } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();

  /**
   * La modale d'abonnement : `null` fermée, sinon le code promo à pré-remplir.
   * Une chaîne vide reste une ouverture — d'où le `null` plutôt qu'un booléen.
   */
  const [signup, setSignup] = useState<string | null>(null);
  const openSignup = (code = ''): void => {
    setSignup(code);
  };

  const pillars = [
    {
      icon: <div className={styles.iconBlob} />,
      title: t(S.saas.p1_title, lang),
      body: t(S.saas.p1_body, lang),
    },
    {
      icon: (
        <div className={styles.iconLegs}>
          <span />
          <span />
        </div>
      ),
      title: t(S.saas.p2_title, lang),
      body: t(S.saas.p2_body, lang),
    },
    {
      icon: <div className={styles.iconRing} />,
      title: t(S.saas.p3_title, lang),
      body: t(S.saas.p3_body, lang),
    },
  ];

  return (
    <div className={styles.root}>
      <header className={styles.nav}>
        <div className={styles.logo}>
          <span className={styles.logoMark} aria-hidden="true" />
          {t(S.common.steve, lang)}
          <span className={styles.trademark} aria-hidden="true">
            ™
          </span>
        </div>
        <div className={styles.navActions}>
          <button type="button" className={styles.pill} onClick={actions.toggleAudio}>
            {audio.label}
          </button>
          <button
            type="button"
            className={cx(styles.pill, styles.pillDark)}
            onClick={() => {
              openSignup();
            }}
          >
            {t(S.saas.cta_adopt, lang)}
          </button>
        </div>
      </header>

      <section className={styles.hero}>
        <div>
          <div className={styles.badge}>{t(S.saas.badge, lang)}</div>
          <h1 className={styles.title}>{t(S.saas.hero, lang)}</h1>
          <p className={styles.subtitle}>{t(S.saas.sub, lang)}</p>

          <div className={styles.ctaRow}>
            <button
              type="button"
              className={styles.ctaPrimary}
              onClick={() => {
                openSignup();
              }}
            >
              {t(S.saas.cta_start, lang)}
            </button>
            <button type="button" className={styles.ctaGhost} onClick={actions.toggleAudio}>
              {t(S.saas.cta_demo, lang)}
            </button>
          </div>

          {/*
            La promesse, puis sa note de bas de page — dans cet ordre et à cette
            distance. C'est la mise en page qui fait la blague : l'astérisque
            est collé sous la ligne qu'il dément, comme partout ailleurs.
          */}
          <p className={styles.trial}>{t(S.saas.trial, lang)}</p>
          <p className={styles.trialFine}>{t(S.saas.trial_fine, lang)}</p>

          <div className={styles.metrics}>
            <div>
              <div className={styles.metricValue}>{seen}</div>
              <div className={styles.metricLabel}>{t(S.common.seen_today, lang)}</div>
            </div>
            <div>
              <div className={styles.metricValue}>2</div>
              <div className={styles.metricLabel}>{t(S.common.legs_live, lang)}</div>
            </div>
            <div>
              <div className={styles.metricValue}>100 %</div>
              <div className={styles.metricLabel}>{t(S.common.orange_pct, lang)}</div>
            </div>
          </div>
        </div>

        <div className={styles.visual}>
          <div className={styles.visualStripes} aria-hidden="true" />
          <div className={styles.steveHolder}>
            <Steve bobDuration="3.4s" onClick={actions.bump} label={t(S.common.title, lang)} />
          </div>
          <div className={styles.figure}>{t(S.saas.fig, lang)}</div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t(S.saas.pillars, lang)}</h2>
        <div className={styles.cards}>
          {pillars.map((pillar) => (
            <article key={pillar.title} className={styles.card}>
              {pillar.icon}
              <h3 className={styles.cardTitle}>{pillar.title}</h3>
              <p className={styles.cardBody}>{pillar.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t(S.saas.mixer, lang)}</h2>
        <div className={styles.mixerGrid}>
          <div className={styles.mixerCard}>
            <div className={styles.pads}>
              {mixer.pads.map((pad) => (
                <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                  <span>{pad.label}</span>
                  <span className={styles.padTime}>{pad.timeLabel}</span>
                </button>
              ))}
            </div>
            <div className={styles.buttons}>
              <button type="button" className={styles.pill} onClick={mixer.onToggleHold}>
                {mixer.holdLabel}
              </button>
              <button type="button" className={styles.ctaPrimary} onClick={mixer.onStop}>
                {mixer.stopLabel}
              </button>
            </div>
          </div>

          <div className={cx(styles.mixerCard, styles.mixerControls)}>
            <VuMeter className={styles.vu} barClassName={styles.vuBar} />

            {mixer.sliders.map((slider) => (
              <div key={slider.key} className={styles.slider}>
                <label className={styles.sliderLabel} htmlFor={`saas-${slider.key}`}>
                  {slider.label}
                </label>
                <input
                  id={`saas-${slider.key}`}
                  type="range"
                  className={styles.sliderInput}
                  min={slider.min}
                  max={slider.max}
                  step={slider.step}
                  value={slider.value}
                  onChange={(event) => {
                    slider.onChange(event.currentTarget.valueAsNumber);
                  }}
                />
                <output className={styles.sliderValue} htmlFor={`saas-${slider.key}`}>
                  {slider.valueLabel}
                </output>
              </div>
            ))}

            <div className={styles.buttonsRow}>
              {mixer.presets.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  className={styles.pill}
                  onClick={preset.onSelect}
                >
                  {preset.label}
                </button>
              ))}
              <button type="button" className={styles.pill} onClick={mixer.onToggleWobble}>
                {mixer.wobbleLabel}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className={cx(styles.section, styles.sectionLast)}>
        <h2 className={styles.sectionTitle}>{t(S.saas.pricing, lang)}</h2>
        <div className={styles.cards}>
          <article className={styles.tier}>
            <div className={styles.tierName}>{t(S.saas.tier1, lang)}</div>
            <div className={styles.tierPrice}>{formatPrice(0, lang)}</div>
            <ul className={styles.tierLines}>
              <li>{t(S.saas.tier1_l1, lang)}</li>
              <li>{t(S.saas.tier1_l2, lang)}</li>
              <li>{t(S.saas.tier1_l3, lang)}</li>
            </ul>
          </article>

          <article className={cx(styles.tier, styles.tierFeatured)}>
            <div className={styles.tierBadge}>{t(S.saas.tier2_badge, lang)}</div>
            <div className={styles.tierName}>{t(S.saas.tier2, lang)}</div>
            <div className={styles.tierPrice}>{formatPrice(12, lang)}</div>
            <ul className={styles.tierLines}>
              <li>{t(S.saas.tier2_l1, lang)}</li>
              <li>{t(S.saas.tier2_l2, lang)}</li>
              <li>{t(S.saas.tier2_l3, lang)}</li>
            </ul>
          </article>

          <article className={styles.tier}>
            <div className={styles.tierName}>{t(S.saas.tier3, lang)}</div>
            <div className={cx(styles.tierPrice, styles.tierPriceWords)}>
              {t(S.saas.tier3_price, lang)}
            </div>
            <ul className={styles.tierLines}>
              <li>{t(S.saas.tier3_l1, lang)}</li>
              <li>{t(S.saas.tier3_l2, lang)}</li>
              <li>{t(S.saas.tier3_l3, lang)}</li>
            </ul>
          </article>
        </div>
      </section>

      <footer className={styles.footer}>
        <div>
          <p className={styles.footerTitle}>{t(S.saas.footer_cta, lang)}</p>
          <p className={styles.footerLine} role="status">
            {audio.hint} · {t(S.common.egg_hint, lang)}
          </p>
          <p className={styles.footerCredits}>
            <Credits linkClassName={styles.footerLink} />
          </p>
        </div>
        <button
          type="button"
          className={styles.footerCta}
          onClick={() => {
            openSignup();
          }}
        >
          {t(S.saas.cta_adopt, lang)}
        </button>
      </footer>

      {/* La Steveletter offre le code promo ; le code ouvre la modale. */}
      <SaasNewsletter onAccept={openSignup} />

      {signup !== null && (
        <SaasSignupModal
          promo={signup}
          onClose={() => {
            setSignup(null);
          }}
        />
      )}
    </div>
  );
}
